/**
 * Delivery Checkout Integration
 * Handles cart attributes, order notes, and order metafields
 */

class DeliveryCheckout {
  constructor() {
    // Try multiple selectors to find checkout button
    this.checkoutButton = document.getElementById('checkout') || 
                          document.querySelector('[name="checkout"]') ||
                          document.querySelector('button[name="checkout"]') ||
                          document.querySelector('#checkout');
    this.cartForm = document.getElementById('cart') || document.querySelector('form[action="/cart"]');
    this.deliveryData = null;
    this.lastButtonState = null; // Track last state to only update when changed

    this.init();
  }

  init() {
    // Clear any existing delivery-formatted notes from cart first
    this.clearDeliveryNoteFromCart();

    // Listen for delivery data updates
    window.addEventListener('deliveryDataUpdated', (e) => {
      this.deliveryData = e.detail;
      this.updateCartAttributes();
      this.updateCheckoutButton();
    });

    // Load existing delivery data from sessionStorage (for cart attributes only)
    this.loadDeliveryData();

    // Ensure checkout button starts disabled/low-opacity until we can verify pickers
    const ensureInitiallyDisabled = () => {
      if (this.checkoutButton) {
        this.checkoutButton.disabled = true;
        this.checkoutButton.setAttribute('disabled', 'disabled');
        this.checkoutButton.classList.add('disabled');
        this.checkoutButton.style.opacity = '0.5';
        this.checkoutButton.style.cursor = 'not-allowed';
      }
    };
    ensureInitiallyDisabled();

    // Wait for delivery calendar to initialize before checking button state
    // Don't change button state until we can properly check the pickers
    // This prevents premature opacity changes
    const checkButtonWhenReady = () => {
      if (window.deliveryCalendar && window.deliveryCalendar.postcodeInput) {
        // Calendar is ready and inputs are available, check button state
        this.updateCheckoutButton();
      } else {
        // Calendar not ready yet, try again (but limit retries)
        if (this._checkRetries === undefined) {
          this._checkRetries = 0;
        }
        if (this._checkRetries < 20) { // Max 20 retries (2 seconds)
          this._checkRetries++;
          setTimeout(checkButtonWhenReady, 100);
        } else {
          console.warn('Delivery checkout - Delivery calendar did not initialize in time, button state not updated');
        }
      }
    };

    // Start checking after a short delay to allow calendar to initialize
    setTimeout(checkButtonWhenReady, 300);

    console.log('Delivery checkout - Initialized');
  }

  loadDeliveryData() {
    try {
      const saved = sessionStorage.getItem('delivery_data');
      if (saved) {
        const savedData = JSON.parse(saved);
        
        // CRITICAL: Don't restore date until we can validate it against current available dates
        // Store the saved data but don't update cart attributes yet
        // Validation will happen in updateCartAttributes() when it's called after calendar is ready
        this.deliveryData = savedData;
        
        console.log('Delivery checkout - Loaded delivery data from sessionStorage:', savedData);
        console.log('Delivery checkout - Will validate date when calendar is ready');
        
        // Don't call updateCartAttributes() here - wait for calendar to be initialized
        // and for delivery windows to be fetched so we can validate the date
      }
      
    } catch (error) {
      console.error('Delivery checkout - Error loading delivery data:', error);
      // Clear corrupted data
      sessionStorage.removeItem('delivery_data');
    }
  }

  async clearDeliveryNoteFromCart() {
    // Check if cart has a note that matches our delivery format
    // Format: "Postcode: X | Date: Y | Time: Z | Servable: A | Test Order: B"
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      
      const cartData = await cartResponse.json();
      const note = cartData.note;
      
      // Check if note matches our delivery format pattern
      if (note && /^Postcode:\s*.+\s*\|\s*Date:\s*.+\s*\|\s*Time:\s*.+\s*\|\s*Servable:\s*.+\s*\|\s*Test Order:\s*.+$/i.test(note.trim())) {
        console.log('Delivery checkout - Found delivery-formatted note in cart, clearing it');
        // Clear the note by setting it to empty string
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: '' })
        });
      }
    } catch (error) {
      console.warn('Delivery checkout - Could not check/clear cart note:', error);
    }
  }

  async updateCartAttributes() {
    // Get fresh delivery data from calendar widget (most up-to-date)
    let deliveryData = this.deliveryData;
    
    // If calendar is available, get the latest data from it
    if (window.deliveryCalendar) {
      const calendarData = window.deliveryCalendar.getDeliveryData();
      if (calendarData && calendarData.postcode && calendarData.date && calendarData.timeSlot) {
        deliveryData = calendarData;
        // Update our stored data
        this.deliveryData = deliveryData;
      }
    }
    
    // Fallback to stored deliveryData if calendar doesn't have complete data
    if (!deliveryData || !deliveryData.postcode || !deliveryData.date || !deliveryData.timeSlot) {
      console.warn('Delivery checkout - Incomplete delivery data, cannot update cart attributes', {
        hasDeliveryData: !!deliveryData,
        hasPostcode: !!deliveryData?.postcode,
        hasDate: !!deliveryData?.date,
        hasTimeSlot: !!deliveryData?.timeSlot
      });
      // Clear attributes if data is incomplete
      this.clearCartAttributes();
      return;
    }

    // CRITICAL VALIDATION: Verify the selected date is actually in the current available dates
    // This prevents stale dates from sessionStorage (e.g., valid yesterday but not today) from being used
    if (window.deliveryCalendar && deliveryData.date) {
      // Check if delivery windows have been loaded (availableDates should have data)
      if (!window.deliveryCalendar.availableDates || window.deliveryCalendar.availableDates.length === 0) {
        console.warn('Delivery checkout - Cannot validate date: delivery windows not loaded yet');
        console.warn('Delivery checkout - Deferring cart update until delivery windows are available');
        // Don't update cart attributes until we can validate
        // This prevents old dates from sessionStorage from being set before current availability is known
        return;
      }
      
      const isDateAvailable = window.deliveryCalendar.isDateAvailable(deliveryData.date);
      if (!isDateAvailable) {
        console.error('Delivery checkout - Selected date is not in current available dates:', deliveryData.date);
        console.error('Delivery checkout - Available dates:', window.deliveryCalendar.availableDates.map(d => d.date));
        console.error('Delivery checkout - This date may have been valid previously but is no longer available');
        
        // Clear invalid data from sessionStorage
        sessionStorage.removeItem('delivery_data');
        
        // Clear invalid data from cart attributes
        this.clearCartAttributes();
        
        // Clear the calendar selection
        if (window.deliveryCalendar) {
          window.deliveryCalendar.selectedDate = null;
          window.deliveryCalendar.selectedTimeSlot = null;
          if (window.deliveryCalendar.dateInput) {
            window.deliveryCalendar.dateInput.value = '';
          }
          if (window.deliveryCalendar.timeInput) {
            window.deliveryCalendar.timeInput.value = '';
          }
          window.deliveryCalendar.resetTime();
        }
        
        // Clear our internal data
        this.deliveryData = null;
        
        return;
      }
      
      // Additional validation: Verify time slots exist for this date
      if (window.deliveryCalendar.deliveryWindows && window.deliveryProxy) {
        const timeSlots = window.deliveryProxy.extractTimeSlotsForDate(
          window.deliveryCalendar.deliveryWindows, 
          deliveryData.date
        );
        
        if (!timeSlots || timeSlots.length === 0) {
          console.error('Delivery checkout - No time slots found for date:', deliveryData.date);
          console.error('Delivery checkout - This suggests the date is not actually available');
          // Same cleanup as above
          sessionStorage.removeItem('delivery_data');
          this.clearCartAttributes();
          if (window.deliveryCalendar) {
            window.deliveryCalendar.selectedDate = null;
            window.deliveryCalendar.selectedTimeSlot = null;
            if (window.deliveryCalendar.dateInput) {
              window.deliveryCalendar.dateInput.value = '';
            }
            if (window.deliveryCalendar.timeInput) {
              window.deliveryCalendar.timeInput.value = '';
            }
            window.deliveryCalendar.resetTime();
          }
          this.deliveryData = null;
          return;
        }
        
        // Validate that the selected time slot is in the available time slots
        if (deliveryData.timeSlot) {
          const matchingSlot = timeSlots.find(slot => 
            slot.id === deliveryData.timeSlot.id || 
            slot.display === deliveryData.timeSlot.display ||
            slot.time === deliveryData.timeSlot.display
          );
          
          if (!matchingSlot) {
            console.error('Delivery checkout - Selected time slot is not available:', deliveryData.timeSlot);
            // Clear just the time slot, keep the date
            if (window.deliveryCalendar) {
              window.deliveryCalendar.selectedTimeSlot = null;
              if (window.deliveryCalendar.timeInput) {
                window.deliveryCalendar.timeInput.value = '';
              }
              window.deliveryCalendar.resetTime();
            }
            this.deliveryData.timeSlot = null;
            // Don't update cart until user selects a valid time slot
            return;
          }
        }
      }
    }

    // All validations passed - save to sessionStorage
    sessionStorage.setItem('delivery_data', JSON.stringify(deliveryData));

    // Prepare cart attributes (customer-visible fields)
    const attributes = {
      'Delivery Date': deliveryData.date,
      'Delivery Time': deliveryData.timeSlot.display || '',
      'Servable': deliveryData.timeSlot.servable ? 'true' : 'false',
      'Test Order': deliveryData.timeSlot.testOrder ? 'true' : 'false'
    };
    
    console.log('Delivery checkout - Updating cart with delivery data:', {
      date: deliveryData.date,
      time: deliveryData.timeSlot.display,
      servable: deliveryData.timeSlot.servable,
      testOrder: deliveryData.timeSlot.testOrder
    });

    // NOTE: We do NOT set the cart note field with delivery data
    // The cart note is for customer-facing delivery instructions
    // Delivery data goes to cart attributes and order metafields only

    // Update cart via Shopify Cart API
    // Combine attributes and metafields, and explicitly remove any old postcode fields
    const allAttributes = {
      ...attributes,
      // Explicitly remove old postcode fields if they exist
      'Delivery Postcode': null,
    };
    
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: allAttributes
          // Note: We intentionally do NOT include 'note' here
          // to avoid overwriting customer delivery instructions
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Delivery checkout - Cart updated:', data);

      // Also update form hidden inputs if they exist
      this.updateFormInputs(attributes);
      
      // Update button state after cart update to ensure it's enabled
      this.updateCheckoutButton();
    } catch (error) {
      console.error('Delivery checkout - Error updating cart:', error);
    }
  }

  updateFormInputs(attributes) {
    if (!this.cartForm) return;

    // Remove existing delivery-related inputs (but NOT the note field)
    // This includes removing any old "Delivery Postcode" inputs
    const existingInputs = this.cartForm.querySelectorAll('[name^="attributes[Delivery"], [name^="attributes[custom.delivery"]');
    existingInputs.forEach(input => {
      // Only remove delivery-related attribute inputs
      // Explicitly remove postcode inputs
      if (input.name.includes('Delivery') || input.name.includes('delivery') || input.name.includes('postcode')) {
        input.remove();
      }
    });

    // Add cart attributes as hidden inputs
    Object.entries(attributes).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `attributes[${key}]`;
      input.value = value;
      this.cartForm.appendChild(input);
    });

    // NOTE: We do NOT update the cart note field
    // The note field is for customer-facing delivery instructions
    // Delivery data is stored in attributes only
  }

  clearCartAttributes() {
    // Clear sessionStorage
    sessionStorage.removeItem('delivery_data');

    // Remove delivery-related inputs from form
    if (this.cartForm) {
      const existingInputs = this.cartForm.querySelectorAll('[name^="attributes[Delivery"], [name^="attributes[custom.delivery"]');
      existingInputs.forEach(input => input.remove());
    }
    
    // Also clear cart attributes via API to remove any existing delivery data
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Delivery Date': null,
            'Delivery Time': null,
            'Servable': null,
            'Test Order': null,
            'Delivery Postcode': null
          }
        })
      }).catch(err => console.warn('Delivery checkout - Error clearing cart attributes:', err));
    } catch (error) {
      console.warn('Delivery checkout - Could not clear cart attributes:', error);
    }
  }

  updateCheckoutButton() {
    // Always get a fresh reference to the button in case it was replaced (e.g., by global.js cloning)
    // This ensures we're working with the actual button in the DOM
    const currentButton = document.getElementById('checkout') || 
                          document.querySelector('[name="checkout"]') ||
                          document.querySelector('button[name="checkout"]') ||
                          document.querySelector('#checkout');
    
    if (!currentButton) {
      console.warn('Delivery checkout - Checkout button not found');
      return;
    }
    
    // Update our reference if it changed
    if (this.checkoutButton !== currentButton) {
      console.log('Delivery checkout - Button reference changed, updating...');
      this.checkoutButton = currentButton;
    }

    // Check CURRENT state of pickers, not saved data
    // Get the current state from the delivery calendar instance
    let hasPostcode = false;
    let hasDate = false;
    let hasTimeSlot = false;

    // Don't update if delivery calendar is not initialized yet
    // This prevents premature opacity changes
    if (!window.deliveryCalendar || !window.deliveryCalendar.postcodeInput) {
      console.log('Delivery checkout - Delivery calendar not initialized yet, skipping button update');
      return;
    }

    // Check current postcode input value
    const postcodeInput = window.deliveryCalendar.postcodeInput;
    hasPostcode = !!(postcodeInput && postcodeInput.value && postcodeInput.value.trim().length > 0);
    
    // Check current date input value
    const dateInput = window.deliveryCalendar.dateInput;
    hasDate = !!(dateInput && dateInput.value && dateInput.value.trim().length > 0);
    
    // Check current time input value
    const timeInput = window.deliveryCalendar.timeInput;
    hasTimeSlot = !!(timeInput && timeInput.value && timeInput.value.trim().length > 0);
    
    // Also check the internal state as backup
    if (!hasDate && window.deliveryCalendar.selectedDate) {
      hasDate = true;
    }
    if (!hasTimeSlot && window.deliveryCalendar.selectedTimeSlot) {
      hasTimeSlot = true;
    }
    
    const isComplete = hasPostcode && hasDate && hasTimeSlot;

    console.log('Delivery checkout - updateCheckoutButton called', {
      hasPostcode,
      hasDate,
      hasTimeSlot,
      selectedDate: window.deliveryCalendar.selectedDate,
      selectedTimeSlot: window.deliveryCalendar.selectedTimeSlot,
      dateInputValue: window.deliveryCalendar.dateInput?.value,
      timeInputValue: window.deliveryCalendar.timeInput?.value,
      lastButtonState: this.lastButtonState,
      isComplete
    });

    // Update disclaimer text based on picker state
    this.updateDisclaimer(hasDate ? window.deliveryCalendar.selectedDate : null);

    // Only skip visual updates if state hasn't changed, but always ensure attributes are correct
    // This prevents the button from getting stuck in a disabled state
    const stateChanged = this.lastButtonState !== isComplete;
    
    if (!stateChanged && isComplete) {
      // State hasn't changed, but if it's complete, double-check the button is actually enabled
      const needsFix = this.checkoutButton.disabled || 
                       this.checkoutButton.hasAttribute('disabled') ||
                       this.checkoutButton.classList.contains('disabled');
      
      if (needsFix) {
        console.log('Delivery checkout - Button state says complete but is still disabled, fixing...', {
          disabled: this.checkoutButton.disabled,
          hasDisabledAttr: this.checkoutButton.hasAttribute('disabled'),
          hasDisabledClass: this.checkoutButton.classList.contains('disabled')
        });
        // Force update even though state hasn't changed
      } else {
        return; // State hasn't changed and button is correctly enabled, no need to update
      }
    }

    this.lastButtonState = isComplete;

    if (isComplete) {
      // Get fresh button reference one more time before enabling (in case it was replaced)
      const freshButton = document.getElementById('checkout') || 
                         document.querySelector('[name="checkout"]') ||
                         document.querySelector('button[name="checkout"]') ||
                         document.querySelector('#checkout');
      
      if (freshButton && freshButton !== this.checkoutButton) {
        console.log('Delivery checkout - Button was replaced, updating reference before enabling');
        this.checkoutButton = freshButton;
      }
      
      // Enable the button - remove ALL disabled states aggressively
      console.log('Delivery checkout - Enabling button, current state:', {
        disabled: this.checkoutButton.disabled,
        hasDisabledAttr: this.checkoutButton.hasAttribute('disabled'),
        hasDisabledClass: this.checkoutButton.classList.contains('disabled'),
        computedCursor: window.getComputedStyle(this.checkoutButton).cursor,
        buttonId: this.checkoutButton.id,
        buttonInDOM: document.contains(this.checkoutButton)
      });
      
      // Remove disabled property and attribute
      this.checkoutButton.disabled = false;
      this.checkoutButton.removeAttribute('disabled');
      
      // Remove disabled class using multiple methods to ensure it's gone
      // Method 1: classList.remove (may need multiple calls)
      let attempts = 0;
      while (this.checkoutButton.classList.contains('disabled') && attempts < 10) {
        this.checkoutButton.classList.remove('disabled');
        attempts++;
      }
      
      // Method 2: Direct className manipulation
      const currentClasses = this.checkoutButton.className.split(/\s+/).filter(c => c && c !== 'disabled');
      this.checkoutButton.className = currentClasses.join(' ');
      
      // Method 3: Verify it's actually gone
      if (this.checkoutButton.classList.contains('disabled')) {
        console.warn('Delivery checkout - Disabled class still present after removal attempts, forcing removal');
        // Force remove by setting className directly
        const baseClasses = ['button'];
        if (this.checkoutButton.classList.contains('is-loading')) {
          baseClasses.push('is-loading');
        }
        this.checkoutButton.className = baseClasses.join(' ');
      }
      
      // Set enabled styles with !important to override any CSS
      this.checkoutButton.style.setProperty('opacity', '1', 'important');
      this.checkoutButton.style.setProperty('cursor', 'pointer', 'important');
      this.checkoutButton.style.setProperty('pointer-events', 'auto', 'important');
      
      // Double-check after a brief delay to ensure it stayed enabled
      setTimeout(() => {
        const stillDisabled = this.checkoutButton.hasAttribute('disabled') || 
                             this.checkoutButton.disabled ||
                             this.checkoutButton.classList.contains('disabled');
        
        if (stillDisabled) {
          console.warn('Delivery checkout - Button was re-disabled, fixing again...', {
            disabled: this.checkoutButton.disabled,
            hasDisabledAttr: this.checkoutButton.hasAttribute('disabled'),
            hasDisabledClass: this.checkoutButton.classList.contains('disabled')
          });
          
          // Aggressively remove all disabled states
          this.checkoutButton.disabled = false;
          this.checkoutButton.removeAttribute('disabled');
          
          // Remove disabled class using multiple methods
          let attempts = 0;
          while (this.checkoutButton.classList.contains('disabled') && attempts < 10) {
            this.checkoutButton.classList.remove('disabled');
            attempts++;
          }
          
          // Direct className manipulation
          const currentClasses = this.checkoutButton.className.split(/\s+/).filter(c => c && c !== 'disabled');
          this.checkoutButton.className = currentClasses.join(' ');
          
          // Force styles with !important
          this.checkoutButton.style.setProperty('cursor', 'pointer', 'important');
          this.checkoutButton.style.setProperty('pointer-events', 'auto', 'important');
          this.checkoutButton.style.setProperty('opacity', '1', 'important');
        } else {
          // Ensure cursor is still pointer even if button wasn't re-disabled
          this.checkoutButton.style.cursor = 'pointer';
          this.checkoutButton.style.setProperty('cursor', 'pointer', 'important');
        }
      }, 100);
      
      console.log('Delivery checkout - Button state after enabling:', {
        disabled: this.checkoutButton.disabled,
        hasDisabledAttribute: this.checkoutButton.hasAttribute('disabled'),
        hasDisabledClass: this.checkoutButton.classList.contains('disabled'),
        className: this.checkoutButton.className,
        actualHTML: this.checkoutButton.outerHTML.substring(0, 200)
      });
      
      // Set up a MutationObserver to prevent the disabled class from being re-added
      if (!this._disabledClassObserver) {
        this._disabledClassObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const hasDisabled = this.checkoutButton.classList.contains('disabled');
              const shouldBeEnabled = this.lastButtonState === true;
              
              if (hasDisabled && shouldBeEnabled) {
                console.warn('Delivery checkout - Disabled class was re-added, removing it again');
                this.checkoutButton.classList.remove('disabled');
                const currentClasses = this.checkoutButton.className.split(/\s+/).filter(c => c && c !== 'disabled');
                this.checkoutButton.className = currentClasses.join(' ');
                this.checkoutButton.style.setProperty('cursor', 'pointer', 'important');
              }
            }
          });
        });
        
        this._disabledClassObserver.observe(this.checkoutButton, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
      
      // Ensure click handler is attached
      if (!this.checkoutButton.hasAttribute('data-checkout-handler-attached')) {
        this.checkoutButton.setAttribute('data-checkout-handler-attached', 'true');
        this.checkoutButton.addEventListener('click', async (e) => {
          if (!this.checkoutButton.disabled && !this.checkoutButton.hasAttribute('disabled')) {
            // Don't proceed if already loading
            if (this.checkoutButton.classList.contains('is-loading')) {
              return;
            }
            
            console.log('Delivery checkout - Checkout button clicked (backup handler)');
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            // Show loading spinner
            this.checkoutButton.classList.add('is-loading');
            this.checkoutButton.disabled = true;
            
            // CRITICAL: Update cart attributes with delivery data before navigating
            try {
              console.log('Delivery checkout - Updating cart attributes before checkout navigation');
              await this.updateCartAttributes();
              console.log('Delivery checkout - Cart attributes updated, navigating to checkout');
            } catch (error) {
              console.error('Delivery checkout - Error updating cart attributes:', error);
              // Continue to checkout even if there's an error
            }
            
            // Navigate to checkout
            window.location.href = '/checkout';
          }
        }, true);
      }
      
      console.log('Delivery checkout - Checkout button enabled (all pickers populated)');
    } else {
      this.checkoutButton.disabled = true;
      this.checkoutButton.setAttribute('disabled', 'disabled');
      this.checkoutButton.classList.add('disabled');
      this.checkoutButton.style.opacity = '0.5';
      this.checkoutButton.style.cursor = 'not-allowed';
      console.log('Delivery checkout - Checkout button disabled', {
        hasPostcode,
        hasDate,
        hasTimeSlot
      });
    }
  }

  updateDisclaimer(selectedDateStr) {
    const disclaimerEl = document.getElementById('checkout-disclaimer');
    if (!disclaimerEl) return;

    const defaultText = disclaimerEl.dataset.default || 'Please select a delivery date to continue';
    const immediateText = disclaimerEl.dataset.immediate || '';
    const authorisedText = disclaimerEl.dataset.authorised || '';

    if (!selectedDateStr) {
      disclaimerEl.innerHTML = defaultText;
      return;
    }

    // Compute if selected date is more than 7 days from now
    const selectedDate = new Date(selectedDateStr);
    const now = new Date();
    const diffDays = (selectedDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

    if (diffDays > 7) {
      disclaimerEl.innerHTML = immediateText || defaultText;
    } else {
      disclaimerEl.innerHTML = authorisedText || defaultText;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.deliveryCheckout = new DeliveryCheckout();
  // Button state will be updated by DeliveryCheckout.init() after calendar is ready
});

// Make class globally available
window.DeliveryCheckout = DeliveryCheckout;
